// Base interface for all database entities
export interface BaseEntity {
  id: string
  createdAt: Date
  updatedAt: Date
  lastModified: Date
  // Sync fields for future Supabase integration
  synced: boolean
  syncedAt: Date | null
}

// Attention levels (how much focus a task needs)
export type AttentionLevel = 'high' | 'medium' | 'low'

// Start period (time-of-day ranges for task scheduling)
export type StartPeriod = 'morning' | 'noon' | 'afternoon' | 'evening'

// Life space — the three top-level areas a task / project / goal belongs to.
export type LifeSpace = 'personal' | 'work' | 'home'

// Task statuses  
export type TaskStatus = 'todo' | 'in_progress' | 'completed' | 'cancelled'

// Task categories
export type TaskCategory = 'work' | 'personal' | 'health' | 'learning' | 'other'

/**
 * Art der **Dauer** (CONTEXT.md): `duration` Minuten/Stunden, `thinking` „Zum Durchdenken“,
 * `days` Tage (#197). `estimatedDuration` bleibt in allen drei Arten in Minuten — ein Tag
 * zählt {@link MINUTES_PER_DAY}, damit Fokus und Überzeit weiter mit Minuten rechnen.
 */
export type DurationMode = 'duration' | 'thinking' | 'days'

/** Ein Tag der **Dauer** sind 8 Std. — so viel wie „Ganzer Tag“ im Picker (#197). */
export const MINUTES_PER_DAY = 480

// Task model
export interface Task extends BaseEntity {
  title: string
  description: string | null
  status: TaskStatus
  attention: AttentionLevel | null
  category: TaskCategory
  /**
   * Stable lifespace affinity. Independent of `projectId` so orphan tasks
   * (projectId == null — e.g. unclarified Kurz-notiert entries) still know
   * which of the three lifespaces they belong to.
   */
  lifeSpace: LifeSpace
  projectId: string | null // local project ID (Dexie UUID)
  sectionId: string | null // local section ID (null = no section)
  userId: string | null // owner (Supabase auth user ID)
  /** @deprecated Legacy sync mapping. Use local_id (= task.id) for cross-device identity. */
  cloudId: string | null
  dueDate: Date | null
  dueTime: string | null // "HH:mm" format, mutually exclusive with duePeriod
  duePeriod: StartPeriod | null // mutually exclusive with dueTime
  startDate: Date | null
  startTime: string | null // "HH:mm" format, mutually exclusive with startPeriod
  startPeriod: StartPeriod | null // mutually exclusive with startTime
  completedAt: Date | null
  estimatedDuration: number | null // in minutes
  actualDuration: number | null // in minutes
  durationMode: DurationMode | null
  tags: string[]
  parentTaskId: string | null // for subtasks
  /**
   * Attached catalogue reward — "was gönnst du dir nach dem Erledigen?".
   * Holds an id from the bundled reward catalogue (see lib/data/rewardCatalog),
   * never free text. A motivation anchor, not a claim: nothing is redeemed and
   * the value survives completion. Same shape as `Habit.rewardId`.
   */
  rewardId: string | null
  /** Die **Wiederholung** — `null` = einmalig. Siehe {@link RecurrenceRule}, ADR 0021. */
  recurrence: RecurrenceRule | null
  order: number // for custom ordering
  /**
   * Position **innerhalb ihres Tages auf „Demnächst"** — `null`, solange
   * niemand die Zeile dort gezogen hat.
   *
   * **Warum nicht `order`?** `order` gehört dem Projekt: `ProjectPage` baut
   * seine Abschnitte, indem es Aufgaben UND Abschnitte in eine nach `order`
   * sortierte Liste mischt und durchläuft — die Zugehörigkeit einer Aufgabe zu
   * ihrem Abschnitt steht also in diesem Wert. Ein Ziehen auf *Demnächst*, das
   * `order` neu schriebe, schöbe die Aufgabe in einen anderen Abschnitt ihres
   * Projekts. Dazu kommt: Zwei Aufgaben desselben Tages stammen meist aus
   * verschiedenen Projekten, ihre `order`-Werte zählen in verschiedenen Räumen
   * und sind gar nicht vergleichbar.
   *
   * Fließkomma wie `order` und `Subtask.sortOrder`: Eine verschobene Zeile
   * nimmt die Mitte zwischen ihren neuen Nachbarn, ein Zug schreibt also genau
   * eine Zeile statt den ganzen Tag neu zu nummerieren.
   *
   * `null` heißt „nie gezogen" und sortiert ans Ende der gezogenen Zeilen, wo
   * dann Uhrzeit und `order` entscheiden — das ist die Reihenfolge, die es vor
   * dem 22.09.2026 überall gab.
   */
  dayOrder: number | null
  deletedAt: Date | null // soft delete
  pausedAt: Date | null // set by Clarify-Mode "Ruhen lassen"; null = active, Date = paused since
  /**
   * **Herkunft** einer importierten Zeile — `todoist:<Todoist-ID>`, sonst `null`.
   * Ein zweiter Import schlägt hier nach und legt nichts doppelt an. Kopien
   * (Duplizieren, nächste Wiederholung) erben es nicht. Siehe `lib/import/importedFrom`.
   */
  importedFrom?: string | null
  /**
   * Nur an einer umgewandelten Unteraufgabe (T14 #179, `convertLegacySubtasks`): die
   * Cloud-Kennung ihrer alten `subtasks`-Zeile. Die neue Aufgaben-Zeile übernimmt sie —
   * so trifft ein zweites Gerät desselben Kontos dieselbe Zeile, statt sie doppelt
   * anzulegen. Wird nicht synchronisiert.
   */
  adoptCloudId?: string | null
  /**
   * **Favorit** (#210): `null`/fehlt = keiner; sonst die Stelle in der Gruppe „Favoriten“ der Sidebar —
   * EINE Reihenfolge für Projekte und Aufgaben eines Lebensbereichs (ms-Zahl, `lib/favorites`).
   * Die nächste Runde einer wiederkehrenden Aufgabe übernimmt ihn, eine Kopie nicht.
   */
  favoriteOrder?: number | null
  /**
   * **Erledigt-Zähler** (#198, CONTEXT.md): wie oft abgehakt; `null`/fehlt = 0. Die Regel steht in
   * `lib/db/taskCounters`. Wie {@link postponedCount} gehört er der wiederkehrenden Aufgabe, nicht
   * der Runde: Der Nachfolger übernimmt ihn, eine Kopie nicht.
   */
  completedCount?: number | null
  /** Wie oft **vertagt** (#198, CONTEXT.md) — das Wann rückte auf einen späteren Tag. `null`/fehlt = 0. */
  postponedCount?: number | null
}

// Section model — groups tasks within a project
export interface Section extends BaseEntity {
  title: string
  projectId: string
  userId: string | null
  /** @deprecated Legacy sync mapping. Use local_id (= section.id) for cross-device identity. */
  cloudId: string | null
  order: number // interleaved with tasks in the same project
  collapsed: boolean
  deletedAt: Date | null
  /** Herkunft eines Imports (`todoist:<id>`), siehe {@link Task.importedFrom}. */
  importedFrom?: string | null
  /**
   * Beschreibung des Abschnitts (#192, aus Todoist). Gespeichert und synchronisiert, aber noch
   * nirgends angezeigt. `null` = keine.
   */
  description?: string | null
  /**
   * **Archiviert** seit (#193), `null` = aktiv. Ein archivierter Abschnitt steht im
   * Geschafft-Schalter der Projektseite, mit seinen erledigten Aufgaben darunter — es sei denn,
   * er hat offene Aufgaben: Dann steht er normal in der Liste, mit der Marke „Archiviert“.
   */
  archivedAt?: Date | null
}

// Settings model - key-value store
export interface Setting extends BaseEntity {
  key: string
  value: any // can be string, number, boolean, object
  type: 'string' | 'number' | 'boolean' | 'object' | 'array'
  description: string | null
  isUserSetting: boolean // true for user settings, false for system settings
}

// User data/preferences
export interface UserProfile extends BaseEntity {
  username: string | null
  email: string | null
  fullName: string | null // For offline mode user name
  displayName: string | null
  avatar: string | null
  /** Selected avatar preset id (resolved to an image via the `avatars` table).
   *  Used for the offline 'offline-user' profile; non-indexed. */
  avatar_preset_id?: string | null
  usagePurposes: string[] // For offline mode: e.g. ['work', 'personal', 'health']
  userIntents: string[] // For offline mode: e.g. ['daily_routines', 'focus']
  preferences: {
    theme: 'light' | 'dark' | 'system'
    language: string
    notifications: boolean
    sounds: boolean
    autoSync: boolean
    workingHours: {
      start: string // HH:mm format
      end: string // HH:mm format
    }
    pomodoroSettings: {
      workDuration: number // minutes
      shortBreakDuration: number // minutes
      longBreakDuration: number // minutes
      cyclesBeforeLongBreak: number
    }
  }
  statistics: {
    totalTasksCompleted: number
    totalTimeSpent: number // minutes
    currentStreak: number // days
    longestStreak: number // days
    lastActiveDate: Date | null
  }
}

/**
 * **Ein Abschnitt Fokuszeit** — von Play bis Pause, bis zum Haken oder bis das
 * Gerät zuklappt. In der Oberfläche heißt das **fokussiert**, nie
 * „Zeiterfassung" (CONTEXT.md).
 *
 * `endTime: null` heißt **läuft gerade**. Davon gibt es höchstens einen, und er
 * ist immer der jüngste: {@link HappyTaskDB.startFocus} beendet den vorigen,
 * bevor es einen neuen anlegt.
 *
 * **Keine `duration`-Spalte.** Die Dauer ergibt sich sekundengenau aus Start
 * und Ende; eine zweite, gerundete Wahrheit daneben verlöre jeden Abschnitt
 * unter einer Minute. Ebenso wenig `description`/`isManual`: In der Beta wird
 * Zeit weder nachgetragen noch korrigiert.
 *
 * **Ein laufender Abschnitt bleibt auf dem Gerät** und geht erst hoch, wenn er
 * beendet ist (ADR 0025) — sonst zeigte das Web den Fokus eines vielleicht
 * abgestürzten Rechners als laufend an.
 */
/**
 * Kürzer als das war ein Fehlklick auf Play, kein Fokus — solche Abschnitte
 * werden verworfen statt gebucht (siehe {@link TimeEntry}).
 */
export const MIN_FOCUS_SECONDS = 5

export interface TimeEntry extends BaseEntity {
  taskId: string
  startTime: Date
  /** `null` = läuft gerade. */
  endTime: Date | null
  userId: string | null
  /** @deprecated Legacy sync mapping. Use local_id (= entry.id) for cross-device identity. */
  cloudId: string | null
  deletedAt: Date | null
}

/**
 * **Ein Kommentar** an einer Aufgabe, auch an einer Unteraufgabe (#182, CONTEXT.md):
 * ein datierter Eintrag, beliebig viele je Aufgabe — anders als die **Notizen**
 * (`Task.description`), die EIN Text sind.
 *
 * `postedAt` ordnet und bleibt, wenn der Text geändert wird; `updatedAt` ist nur
 * die Sync-Uhr. `content` ist Klartext. Beim Abhaken einer wiederkehrenden Aufgabe
 * wandern die Kommentare zur nächsten Runde (ADR 0021, Nachtrag) — `taskId` ändert sich.
 */
export interface TaskComment extends BaseEntity {
  taskId: string
  content: string
  postedAt: Date
  /** Herkunft eines Imports (`todoist:<note id>`), siehe {@link Task.importedFrom}. */
  importedFrom: string | null
  userId: string | null
  /** @deprecated Legacy sync mapping. Use local_id (= comment.id) for cross-device identity. */
  cloudId: string | null
  deletedAt: Date | null
}

/**
 * **Ein Kommentar an einem Projekt** (#184, CONTEXT.md): dieselbe Sache wie {@link TaskComment},
 * nur am Projekt. Eine eigene Tabelle statt `projectId` an den Aufgaben-Kommentaren — ältere
 * App-Versionen erwarten an jedem Aufgaben-Kommentar eine Aufgabe; diese Tabelle kennen sie nicht.
 */
export interface ProjectComment extends BaseEntity {
  projectId: string
  content: string
  postedAt: Date
  /** Herkunft eines Imports (`todoist:<project note id>`). */
  importedFrom: string | null
  userId: string | null
  /** @deprecated Legacy sync mapping. Use local_id (= comment.id) for cross-device identity. */
  cloudId: string | null
  deletedAt: Date | null
}

export type ReminderKind = 'absolute' | 'relative'
/** Worauf sich eine „vorher“-Erinnerung bezieht: das **Wann** (`start*`) oder die Deadline (`due*`). */
export type ReminderAnchor = 'start' | 'deadline'

/**
 * **Eine Erinnerung an einer Aufgabe** (CONTEXT.md, Plan `.claude/tasks/task-reminders.md`) —
 * Modell wie Todoist: eine eigene Zeile, beliebig viele je Aufgabe.
 *
 * - `relative`: `offsetMinutes` vor dem Wann bzw. der Deadline (`anchor`). **`offsetMinutes: null`
 *   ist die Standard-Erinnerung**: Ihr Vorlauf steht in den Einstellungen, nicht an ihr — wer ihn
 *   ändert oder ausschaltet, ändert alle. Ohne Uhrzeit am Bezug bleibt sie still.
 * - `absolute`: `remindDate` + `remindTime` als Wanduhr wie die Uhrzeiten der Aufgaben, mit
 *   `recurrence` (nur `fixed`) als eigener Rhythmus.
 *
 * `snoozedUntil` ist ein fester Zeitpunkt, kein Wanduhr-Wert: „in 15 Min.“ meint genau dann.
 * Beim Abhaken einer wiederkehrenden Aufgabe wandert sie zum Nachfolger (ADR 0021) — `taskId` ändert sich.
 */
export interface TaskReminder extends BaseEntity {
  taskId: string
  kind: ReminderKind
  anchor: ReminderAnchor | null
  offsetMinutes: number | null
  /** `"YYYY-MM-DD"` */
  remindDate: string | null
  /** `"HH:mm"` */
  remindTime: string | null
  recurrence: RecurrenceRule | null
  snoozedUntil: Date | null
  /** Herkunft eines Imports (`todoist:<reminder id>`), siehe {@link Task.importedFrom}. */
  importedFrom: string | null
  userId: string | null
  /** @deprecated Legacy sync mapping. Use local_id (= reminder.id) for cross-device identity. */
  cloudId: string | null
  deletedAt: Date | null
}

/** Eine Erinnerung, bevor sie eine Zeile ist — beim Anlegen, mit der Aufgabe (`CreateTask.reminders`) oder allein. */
export type ReminderDraft =
  | { kind: 'relative'; anchor: ReminderAnchor; offsetMinutes: number | null; importedFrom?: string | null }
  | { kind: 'absolute'; remindDate: string; remindTime: string; recurrence?: RecurrenceRule | null; importedFrom?: string | null }

/** Was sich an einer Erinnerung ändern lässt. */
export type ReminderChanges = Partial<Pick<TaskReminder, 'anchor' | 'offsetMinutes' | 'remindDate' | 'remindTime' | 'recurrence' | 'snoozedUntil'>>

/** Die **Standard-Erinnerung**: relativ, ohne eigenen Vorlauf. */
export const isStandardReminder = (reminder: Pick<TaskReminder, 'kind' | 'offsetMinutes'>): boolean =>
  reminder.kind === 'relative' && reminder.offsetMinutes === null

/** Ein Kommentar, der mit seiner Aufgabe entsteht (`CreateTask.comments`, Todoist-Import in #183). */
export interface CommentDraft {
  content: string
  /** Vorgabe: jetzt. */
  postedAt?: Date
  importedFrom?: string | null
}

// Tags for organizing tasks
export interface Tag extends BaseEntity {
  name: string
  color: string // hex color code
  description: string | null
  usageCount: number
  userId: string | null
  /** @deprecated Legacy sync mapping. Use local_id (= tag.id) for cross-device identity. */
  cloudId: string | null
  deletedAt: Date | null
  /** Herkunft eines Imports (`todoist:<id>`), siehe {@link Task.importedFrom}. */
  importedFrom?: string | null
}

// Projects to group tasks
export interface Project extends BaseEntity {
  name: string
  description: string | null
  color: string // hex color code
  status: 'active' | 'completed' | 'archived'
  lifeSpace: LifeSpace
  userId: string | null
  /** @deprecated Legacy sync mapping. Use local_id (= project.id) for cross-device identity. */
  cloudId: string | null
  startDate: Date | null
  endDate: Date | null
  progress: number // 0-100 percentage
  order: number
  deletedAt: Date | null
  /** System projects are hidden from the regular project list and back features like
   *  Quick Notes. They are created on demand and owned by the app, not the user. */
  isSystem?: boolean
  /** Identifier for the kind of system project (e.g. "quick-notes"). */
  systemKind?: string | null
  /**
   * Projekt-Canvas panel state — persisted and synced so Electron and Web show
   * the same panel across devices. `null` = never opened (closed/peek, derived
   * from note count, not stored here).
   */
  canvasPanelState?: 'closed' | 'split' | 'maximized' | null
  /** Split ratio (0-1, share of width given to the task list) while `split`. `null` = default 50/50. */
  canvasSplitRatio?: number | null
  /**
   * Projekt-Icon (CONTEXT.md „Projekt-Kopf“): `happy:<rewardId>`,
   * `emoji:<zeichen>` oder `lucide:<name>`. `null` = Standard-Icon.
   */
  icon?: string | null
  /** Eine der 16 Titel-Farben (`lib/projectIcon`), `null` = Ton des Lebensbereichs. */
  iconColor?: string | null
  /**
   * Der gestaltete Titel als HTML-Teilmenge (ADR 0029/0030). `name` ist sein
   * Klartext; wer nur `name` ändert, setzt dies auf `null`.
   */
  nameHtml?: string | null
  /** Herkunft eines Imports (`todoist:<id>`), siehe {@link Task.importedFrom}. */
  importedFrom?: string | null
  /**
   * Das **Elternprojekt** eines Unterprojekts (#185), `null` = oben im Lebensbereich.
   * Höchstens `MAX_PROJECT_DEPTH` Ebenen (`lib/projectTree`); beide im selben Lebensbereich.
   */
  parentId?: string | null
  /** **Favorit** (#210), siehe {@link Task.favoriteOrder}. */
  favoriteOrder?: number | null
}

// Goal model — a statement of direction for one life space.
// A Goal is NOT a task: it is never completed, checked off, or measured.
// It only expresses what currently matters to the user. See CONTEXT.md.
export interface Goal extends BaseEntity {
  title: string
  description: string | null // the optional "why"
  lifeSpace: LifeSpace
  userId: string | null // owner (Supabase auth user ID)
  /** Server-assigned cloud row id (PK). Used by sync push to target updates/
   *  deletes; `id` (= local_id) is the stable cross-device identity. */
  cloudId: string | null
  deletedAt: Date | null // soft delete
  /** Herkunft (`testdata:<lauf>:<n>`), siehe {@link Task.importedFrom}. */
  importedFrom?: string | null
}

// ============================================================
// Habits (Gewohnheiten)
//
// A Habit is a recurring intention checked off on the days it is "due".
// Distinct from Task and Goal — it carries its own recurrence model,
// per-day completion log, streak and optional reward/timer.
// See .claude/tasks/habits-feature.md for the full spec.
// ============================================================

// 0 = Monday … 6 = Sunday (Monday-first, matches DE week + Mo–So quota week)
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6

// Part-of-day slot for the "Tageszeit" frequency. Also the list sort key.
export type HabitTimeSlot = 'morning' | 'noon' | 'evening'

/**
 * Recurrence value object — answers "on which days is this habit due?".
 * - daily        → every day (Mo–So)
 * - timeOfDay    → every day, anchored to a part of day (drives list grouping/sort)
 * - weekdays     → fixed weekday set (covers Mo–Fr, once-a-week, multiple fixed days)
 * - timesPerWeek → flexible weekly quota (no fixed day; streak counts in weeks)
 * - monthly      → on a day-of-month (clamped to month length, e.g. 31 → 28/29 Feb)
 * - interval     → every N days/weeks/months; weekdays optional for unit 'week'
 */
export type HabitFrequency =
  | { kind: 'daily' }
  | { kind: 'timeOfDay'; slot: HabitTimeSlot }
  | { kind: 'weekdays'; days: Weekday[] }
  | { kind: 'timesPerWeek'; count: number }
  | { kind: 'monthly'; day: number }
  | { kind: 'interval'; every: number; unit: 'day' | 'week' | 'month'; weekdays?: Weekday[] }

/**
 * Die **Wiederholung** einer Aufgabe — drei Mechanismen, drei Formen (CONTEXT.md, Wiederkehrend):
 * `fixed` hängt am Kalender, `after_done` am Erledigen der vorigen Aufgabe, `quota` an einer
 * Anzahl je Zeitraum. Presets (Täglich, Werktags …) sind reine UI-Voreinstellungen und stehen
 * hier NICHT. Wochentage: `Weekday` 0 = Mo … 6 = So, wie bei `HabitFrequency`. Gespeichert als
 * Objekt (Dexie) / `jsonb` (Supabase); `null` = einmalige Aufgabe. ADR 0021.
 */
export type RecurrenceUnit = 'day' | 'week' | 'month' | 'year'
/**
 * „Der **3. Freitag** im Monat“, „der **letzte** Freitag“ (`nth: -1`). Steht es an
 * einer Monatsregel, gilt es statt `monthDay`; `monthDay` bleibt trotzdem gefüllt —
 * ältere App-Versionen, die das Feld nicht kennen, lesen dann „am Tag X“.
 * (T11 #176, T12 #177.) „Letzter Tag im Monat“ braucht es nicht: `monthDay: 31`.
 */
export interface NthWeekday {
  nth: 1 | 2 | 3 | 4 | -1
  weekday: Weekday
}
export type QuotaPeriod = 'week' | 'month' | 'year'
/**
 * **Das Ende einer Wiederholung** (T13 #178). Fehlt es, läuft sie endlos („Nie“).
 * `count` zählt an der Regel selbst rückwärts: `remaining` schließt diese Runde ein,
 * ihr Nachfolger trägt eins weniger — so braucht es keine Verknüpfung der Runden
 * (ADR 0021). `date` ist der letzte Tag, an dem noch eine Runde stehen darf.
 */
export type RecurrenceEnd =
  | { kind: 'count'; remaining: number }
  | { kind: 'date'; date: string } // 'YYYY-MM-DD'
export type RecurrenceRule = (
  | { type: 'fixed'; interval: number; unit: 'day' }
  | { type: 'fixed'; interval: number; unit: 'week'; weekdays: Weekday[] }
  | { type: 'fixed'; interval: number; unit: 'month'; monthDay: number; nthWeekday?: NthWeekday }
  | { type: 'fixed'; interval: number; unit: 'year'; yearMonth: number; yearDay: number }
  | { type: 'after_done'; interval: number; unit: RecurrenceUnit }
  | { type: 'quota'; count: number; period: QuotaPeriod }
) & { end?: RecurrenceEnd }

// Habit definition
export interface Habit extends BaseEntity {
  title: string
  notes: string | null
  lifeSpace: LifeSpace
  frequency: HabitFrequency
  /** "ab wann" — habit is due from this day onward. Not due / not counted as
   *  missed before it. Also the anchor for interval frequencies. Default = today. */
  startDate: Date
  rewardId: string | null // attached catalog reward (optional)
  targetMinutes: number | null // optional focus-timer duration
  pausedAt: Date | null // paused/archived (frozen, resumable); null = active
  sortOrder: number // stable order within a life space
  userId: string | null // owner (Supabase auth user ID)
  cloudId: string | null // server-assigned PK; `id` (= local_id) is cross-device identity
  deletedAt: Date | null // soft delete
  /** Herkunft (`testdata:<lauf>:<n>`), siehe {@link Task.importedFrom}. */
  importedFrom?: string | null
}

// Per-day completion log. One stable row per (habitId, date); toggling flips
// `completed` rather than create/delete (cleaner LWW sync). date = 'YYYY-MM-DD' local.
export interface HabitLog extends BaseEntity {
  habitId: string // local habit.id
  date: string // 'YYYY-MM-DD' (local day)
  completed: boolean
  userId: string | null
  cloudId: string | null
  deletedAt: Date | null
}

// ============================================================
// Reward usage ("Zuletzt benutzt")
//
// One row per reward the user has ever picked — NOT a log of every pick.
// Picking a reward again refreshes `usedAt` instead of adding a row, which
// keeps the table at catalogue size and makes last-write-wins sync trivial.
// Feeds the "Zuletzt benutzt" group at the top of the reward picker.
// ============================================================
/**
 * A checklist item under a task. Deliberately its own table rather than a
 * `parentTaskId` task: a subtask is title + tick and nothing else, and keeping
 * it out of `tasks` means no existing task query has to learn to filter it out.
 *
 * Completion is NOT coupled to the parent — ticking the last subtask leaves the
 * task open, and completing the task leaves the subtasks alone.
 */
export interface Subtask extends BaseEntity {
  /** Owning task (local Dexie id). */
  taskId: string
  title: string
  done: boolean
  /**
   * Position within its task — the checklist is a sequence, not a bag.
   *
   * Fractional: a moved row takes the midpoint between its new neighbours, so a
   * drag writes exactly one row instead of renumbering the range it crossed.
   * The cloud column is DOUBLE PRECISION for the same reason (migration
   * `20260920120000`), as `tasks."order"` has been since `014_order_to_float`.
   */
  sortOrder: number
  userId: string | null
  cloudId: string | null
  deletedAt: Date | null
  /** Herkunft eines Imports (`todoist:<id>`), siehe {@link Task.importedFrom}. */
  importedFrom?: string | null
  /*
    **Nur in der Ansicht** (T14 #179): Seit Unteraufgaben Aufgaben sind, reicht die
    Datenquelle ihre Termine und die Aufmerksamkeit mit, damit die Zeile der Checkliste
    sie zeigen kann. Die alte Tabelle `subtasks` kennt sie nicht.
    Uhrzeit, Dauer und Tags braucht das **Aufgaben-Menü** an der Zeile (#205): Es zeigt
    die gesetzten Werte in seinen Kästen. Das Ruhen zeigt die Zeile selbst.
  */
  attention?: AttentionLevel | null
  startDate?: Date | null
  startTime?: string | null
  startPeriod?: StartPeriod | null
  dueDate?: Date | null
  dueTime?: string | null
  duePeriod?: StartPeriod | null
  estimatedDuration?: number | null
  durationMode?: DurationMode | null
  tags?: string[]
  /** Eigenes Ruhen (#205): Die Zeile steht gedämpft in der Checkliste. */
  pausedAt?: Date | null
  /** Eigene Belohnung (#205): ein Zeichen an der Zeile, der Geschafft-Moment beim Abhaken. */
  rewardId?: string | null
  /** Eigene Wiederholung (#205): ein Zeichen an der Zeile, beim eigenen Abhaken ein Nachfolger. */
  recurrence?: RecurrenceRule | null
}

export type CreateSubtask = Omit<
  Subtask,
  keyof BaseEntity | 'done' | 'sortOrder' | 'userId' | 'cloudId' | 'deletedAt'
> & {
  done?: boolean
  sortOrder?: number
  userId?: string | null
}

export type UpdateSubtask = Partial<Pick<Subtask, 'title' | 'done' | 'sortOrder' | 'deletedAt'>>

// ============================================================
// Projekt-Canvas (Notizen & Memos)
//
// A free-form idea-collection surface per project (see .claude/tasks/project-canvas.md
// and ADR 0004). Deliberately its own entity, not a Task variant: a canvas note
// carries geometry (position, size, color) that no Task has, and none of what
// makes a Task a Task (due date, reward, subtasks, timer). It only becomes a
// Task through explicit **Übernehmen** (`taskId`/`convertedAt`).
// ============================================================
export interface CanvasNote extends BaseEntity {
  projectId: string
  /**
   * UI: 'sticky' = "Notiz" (adaptive font size, no title), 'text' = "Memo"
   * (grows with content, optional title), 'area' = "Bereich" (eine Karte mit
   * **Reiter**, die Zettel räumlich zusammenfasst).
   *
   * Der **Bereich** benutzt von dieser Zeile nur Geometrie, `title` und
   * `color`. `content` bleibt leer, `font`, `showAuthor`, `icon`, `taskId`,
   * `convertedAt` und `convertedContent` rührt er nie an — er ist keine
   * Äußerung und wird nie **übernommen**.
   *
   * 'text' = "Text" — freier Text auf der Fläche, ohne Papier (CONTEXT.md,
   * „Text“). Seine Maße folgen immer dem Inhalt; `width` und `height` sind
   * Messwerte wie die Höhe des Memos. `content` hält eine HTML-Teilmenge
   * (ADR 0029), nicht Klartext.
   */
  kind: 'note' | 'memo' | 'area' | 'text'
  /** Optional H1 title — only meaningful for kind 'memo'; always null for 'note'. */
  title: string | null
  content: string
  x: number
  y: number
  width: number
  /** For kind 'memo' this is derived from content, not manually set. */
  height: number
  /**
   * Die **Neigung** — wie schräg das Element liegt, in Grad **im
   * Uhrzeigersinn**. `0` heißt gerade.
   *
   * Genau so, wie CSS `rotate` es liest: keine Umrechnung zwischen Speicherung
   * und Darstellung. (Der **Klebestreifen** zählt in seiner Zeile umgekehrt —
   * das Vorzeichen dreht `canvasTapes.ts` an einer Stelle und erklärt es dort.)
   *
   * **Sie ist Darstellung, nicht Geometrie** (ADR 0015): `x`, `y`, `width` und
   * `height` bleiben achsparallel, und mit ihnen **Einrasten**,
   * **Hilfslinien**, **Bereichs-Zugehörigkeit**, **Platz tauschen** und das
   * Skalieren. Die Ecken des geneigten Papiers ragen über das Rechteck hinaus.
   *
   * Zettel und Memos rasten in **7°-Stufen von −42 bis 42**
   * (`canvasNoteRotations`) — Schrittweite und Grenze sind vom
   * **Klebestreifen** übernommen, damit auf dem Canvas nicht zwei Sprachen für
   * dieselbe Sache stehen. Beim **Bereich** immer `0`: Er fasst Zettel über
   * achsparallele Rechtecke zusammen.
   *
   * **Kein `null`.** Ein Element hat immer eine Neigung, und `0` ist eine
   * gültige Antwort — anders als beim `tape`, wo `null` „nicht geklebt" heißt.
   * Beim Anlegen `0` und nicht gewürfelt: Schräg wird nur, was die Hand schräg
   * legt (siehe CONTEXT.md, „Neigung").
   */
  rotation: number
  /** Palette key (e.g. 'yellow' | 'blue' | ...), valid for both kinds. */
  color: string | null
  /** Emoji set via the selection options bar. */
  icon: string | null
  /**
   * Ob das Element seinen Autor zeigt.
   *
   * **Für beide Arten, aber verschieden dargestellt:** Die **Notiz** zeigt
   * Avatar UND Vorname in einer Pille unter dem Text, der **Notiz** nur den
   * Avatar unten links. Grund ist seine Größe — er darf bis auf
   * `MIN_CANVAS_NOTE_SIZE` (72) schrumpfen, und die Pille wäre dort breiter
   * als der Zettel.
   *
   * Aus, solange niemand ihn anschaltet: Auf einem Canvas, an dem nur eine
   * Person arbeitet, sagt der eigene Name nichts.
   *
   * Der **Titel** braucht kein solches Feld — dort trägt `title` die Auskunft
   * doppelt: `null` heißt „kein Titel", ein String (auch der leere) heißt
   * „Titel an".
   */
  showAuthor: boolean
  /**
   * Die **Schrift** des Elements — ein Schlüssel aus `CANVAS_NOTE_FONTS`.
   *
   * `null` heißt **Standard**, und Standard ist für BEIDE Arten dieselbe
   * Schrift (die der App). Ein Schlüssel, der je nach `kind` etwas anderes
   * bedeutete, wäre in einer gemischten Auswahl nicht mehr zu erklären —
   * siehe „Schrift eines Canvas-Elements" in CONTEXT.md.
   *
   * Der Schlüssel und nicht der Font-Stack: Der Stack ist eine Frage der
   * Darstellung und ändert sich mit den geladenen Dateien; was der Nutzer
   * gewählt hat, ändert sich nicht.
   */
  font: string | null
  /**
   * Die **Schriftgröße** eines **Texts** in px bei Zoom 1 — sein Grundwert;
   * Abweichungen einzelner Stellen stehen im HTML (ADR 0029).
   *
   * Nur bei `kind: 'text'`; alle anderen Arten tragen `null` (die Notiz rechnet
   * ihre Schrift aus der Fläche, das Memo hat eine feste).
   */
  fontSize: number | null
  /**
   * Die **Ausrichtung** eines **Texts** — sie gilt immer dem ganzen Text.
   * `null` heißt linksbündig. Nur bei `kind: 'text'`.
   */
  textAlign: CanvasTextAlign | null
  /**
   * **Vorn und hinten** — wo das Element in der Überdeckung liegt; größer
   * heißt weiter vorn (CONTEXT.md, „Vorn und hinten").
   *
   * `null` heißt **nach Alter**: Gezählt wird dann `createdAt` in ms. So
   * behalten bestehende Elemente ihre Reihenfolge, ohne dass eine Zeile
   * umgeschrieben wird, und ein neues liegt wie bisher obenauf. Gesetzt wird
   * das Feld nur von „Ganz nach vorn" / „Ganz nach hinten" — auf den größten
   * Wert der Ebene + 1 bzw. den kleinsten − 1 (`utils/canvasZOrder.ts`).
   *
   * Bereiche liegen unabhängig davon immer unter allen Zetteln; der Wert
   * ordnet sie nur untereinander.
   */
  zOrder: number | null
  /**
   * Die **Verbindung** zu einer Aufgabe — gesetzt beim **Übernehmen**. Der
   * Notiz bleibt danach auf dem Canvas liegen, unverändert.
   *
   * Sie ist eine **Herkunft, keine Kopplung**: Es fließt nichts. Sie verhindert
   * eine zweite Übernahme desselben Elements, sie zeigt das **Eselsohr**, und
   * sie ist der Weg zurück zur Aufgabe (siehe CONTEXT.md, „Verbindung").
   */
  /**
   * Der **Bereich**, zu dem dieses Element gehört — oder `null`.
   *
   * **Gemerkt, nicht gerechnet** (ADR 0012): Entschieden wird beim Loslassen,
   * es gewinnt der Bereich, der den größeren Teil des Elements bedeckt,
   * mindestens aber die Hälfte. Überlappen sich zwei Bereiche, kann die
   * Geometrie „genau einer" nicht beantworten — deshalb entscheidet die Hand,
   * und der **Aufnahme-Umriss** zeigt es, während man zieht.
   *
   * **Das Gemerkte darf dem Auge nie widersprechen**: Jede Geste, die die Lage
   * ändert, rechnet es neu — das Ablegen eines Elements, das Ablegen eines
   * Bereichs, das Ändern seiner Größe.
   *
   * Bei `kind: 'area'` immer `null`: Bereiche liegen in keinem Bereich.
   */
  areaId: string | null
  /**
   * Der **Klebestreifen** — seine ganze Erscheinung als `muster:lage:winkel`
   * (`'washi-2:top:0'`, `'washi-1:corner:14'`), oder `null` für „nicht
   * geklebt".
   *
   * **Die Anwesenheit des Streifens IST die Sperre.** Ein zweites `locked`
   * daneben könnte ihr widersprechen; dasselbe Muster wie `title: null` =
   * „kein Titel".
   *
   * Ein geklebtes Element klebt da, wo es liegt — am Canvas, wenn es frei
   * ist; am **Bereich**, wenn es zu einem gehört. Es lässt sich nicht
   * bewegen, bearbeiten, färben, löschen oder übernehmen; für die
   * Ordnungslogik ist es **Anker und Wand** zugleich (siehe CONTEXT.md,
   * „Klebestreifen").
   *
   * **Gewürfelt wird EINMAL, beim Ankleben** (`drawCanvasTape`) — wer ablöst
   * und neu anklebt, zieht ein anderes Stück aus der Schachtel. Deshalb steht
   * die Erscheinung hier und wird nicht beim Zeichnen gerechnet: Ein Würfel im
   * Rendern fiele bei JEDEM Neuaufbau des Nodes neu, und der Streifen
   * flackerte. (Bis 2026-09-06 wurde sie aus der Kennung abgeleitet — stabil,
   * aber ein Zettel behielt sein Stück für immer.)
   *
   * **Drei Angaben in EINEM Feld, kein zweites daneben:** Sie gehören zusammen
   * und ändern sich zusammen; getrennt könnten sie einander widersprechen (eine
   * Neigung auf der Oberkante gibt es nicht).
   *
   * Bei `kind: 'area'` immer `null`: Ein **Bereich** bekommt keinen Streifen.
   */
  tape: string | null
  taskId: string | null
  convertedAt: Date | null
  /**
   * Der Text, wie er BEI DER ÜBERNAHME dastand — die Grundlage für das
   * „bearbeitet" neben dem **Eselsohr**.
   *
   * Ein Vergleich und kein Zeitstempel, weil ⌘Z auf dem Canvas ein Alltagsweg
   * ist: Wer eine Änderung zurücknimmt, hat nichts geändert, und ein
   * Zeitstempel behauptete das Gegenteil.
   */
  convertedContent: string | null
  userId: string | null
  cloudId: string | null
  deletedAt: Date | null
}

export type CanvasTextAlign = 'left' | 'center' | 'right'

export type CreateCanvasNote = Omit<
  CanvasNote,
  keyof BaseEntity | 'title' | 'width' | 'height' | 'rotation' | 'color' | 'icon' | 'showAuthor' | 'font' | 'fontSize' | 'textAlign' | 'zOrder' | 'areaId' | 'tape' | 'taskId' | 'convertedAt' | 'convertedContent' | 'userId' | 'cloudId' | 'deletedAt'
> & {
  title?: string | null
  areaId?: string | null
  tape?: string | null
  width?: number
  height?: number
  rotation?: number
  color?: string | null
  icon?: string | null
  showAuthor?: boolean
  font?: string | null
  fontSize?: number | null
  textAlign?: CanvasTextAlign | null
  userId?: string | null
}

export type UpdateCanvasNote = Partial<
  Pick<CanvasNote, 'title' | 'content' | 'x' | 'y' | 'width' | 'height' | 'rotation' | 'color' | 'icon' | 'showAuthor' | 'font' | 'fontSize' | 'textAlign' | 'zOrder' | 'areaId' | 'tape' | 'taskId' | 'convertedAt' | 'convertedContent' | 'deletedAt'>
>

export interface RewardUsage extends BaseEntity {
  /** Id from the bundled reward catalogue (see lib/data/rewardCatalog). */
  rewardId: string
  /** When the reward was last attached to a task. */
  usedAt: Date
  userId: string | null
  cloudId: string | null
  deletedAt: Date | null
}

// `rewardId` stays required — it is the whole point of the row.
export type CreateRewardUsage = Omit<
  RewardUsage,
  keyof BaseEntity | 'usedAt' | 'userId' | 'cloudId' | 'deletedAt'
> & {
  usedAt?: Date
  userId?: string | null
}

/**
 * A synced UI preference. The row id IS the {@link key} — the same key is
 * derived independently on every device, so both converge on one row and
 * last-write-wins settles the value (same trick as {@link RewardUsage}).
 *
 * Not for theme/mode/language (needed before first paint and before login),
 * window bounds or display mode (device-specific), or sync bookkeeping.
 */
export interface UserPreference extends BaseEntity {
  /** Namespaced key, e.g. `rewardPicker.categoryIds`. Equals `id`. */
  key: string
  /** Any JSON-serialisable value — stored as jsonb in Supabase. */
  value: unknown
  userId: string | null
  cloudId: string | null
  deletedAt: Date | null
}

// `key` and `value` stay required — an entry without either is meaningless.
export type CreateUserPreference = Omit<
  UserPreference,
  keyof BaseEntity | 'userId' | 'cloudId' | 'deletedAt'
> & {
  userId?: string | null
}

/**
 * **Was mit einer gelöschten Aufgabe gegangen ist** — die Kennungen, die
 * `deleteTask` mitgenommen hat, damit das Rückgängig genau sie zurückholt.
 *
 * Kennungen und kein Zeitstempel: Ein Subtask, den jemand einen Moment vorher
 * von Hand gelöscht hat, kann dieselbe Millisekunde tragen, und das Rückgängig
 * darf ihn nicht mit wiederbeleben.
 */
/**
 * Was das Löschen eines **Projekts** mitgenommen hat — für den Rückweg.
 * Kennungen statt eines Zeitstempels, aus demselben Grund wie bei
 * {@link TaskCascade}: Eine Aufgabe, die der Nutzer selbst gelöscht hat, darf
 * das Rückgängig nicht mit wiederbeleben.
 */
export interface ProjectCascade {
  taskIds: string[]
  sectionIds: string[]
  /** **Pinnwand-Elemente** des Projekts (Notizen, Memos, Bereiche). */
  canvasNoteIds: string[]
  /** **Kommentare** der Aufgaben, die mitgegangen sind (#182). */
  commentIds: string[]
  /** **Kommentare** am Projekt selbst (#184). Fehlt bei einer Kaskade von vor #184. */
  projectCommentIds?: string[]
  /** **Erinnerungen** der Aufgaben, die mitgegangen sind. Fehlt bei einer Kaskade von vor den Erinnerungen. */
  reminderIds?: string[]
  /**
   * Die **Unterprojekte**, die mit dem Elternprojekt gegangen sind (#185), beliebig tief. Ihre
   * Aufgaben, Abschnitte usw. stehen in den Listen oben. Fehlt bei einer Kaskade von vor #185.
   */
  subprojectIds?: string[]
}

/**
 * Was das Verschieben eines **Projekts** in einen anderen **Lebensbereich** geändert hat (#220)
 * — der alte Stand jeder Zeile, damit Rückgängig genau ihn zurückbringt.
 */
export interface ProjectLifeSpaceMove {
  projectId: string
  /**
   * Wo das Projekt vorher stand: Lebensbereich, Platz in dessen Liste und — war es ein
   * Unterprojekt — sein Elternprojekt (#185); allein umgezogen löst es sich davon.
   */
  from: { lifeSpace: LifeSpace; order: number; parentId?: string | null }
  /** Die mitgezogenen **Unterprojekte** mit ihrem vorigen Platz (#185). Fehlt bei einem Umzug von vor #185. */
  subprojects?: { id: string; lifeSpace: LifeSpace; order: number }[]
  /** Jede mitgezogene Aufgabe mit ihrem vorigen Lebensbereich. */
  tasks: { id: string; lifeSpace: LifeSpace }[]
}

export interface TaskCascade {
  subtaskIds: string[]
  /** Canvas-Elemente, deren **Verbindung** gelöst wurde (die Notiz selbst bleibt). */
  unlinkedNoteIds: string[]
  /** Abschnitte **Fokuszeit**, die mit der Aufgabe weich gelöscht wurden. */
  timeEntryIds: string[]
  /** **Kommentare** des ganzen Asts, die mit ihm weich gelöscht wurden (#182). */
  commentIds: string[]
  /** **Erinnerungen** des ganzen Asts. Fehlt bei einer Kaskade von vor den Erinnerungen. */
  reminderIds?: string[]
}

/** Eine Zeile der Checkliste als Baum (#181). */
export interface SubtaskTreeRow extends Subtask {
  /** Ebene unter der Aufgabe, deren Checkliste gezeigt wird: 1 = direkt darunter. */
  depth: number
  /** Die direkten Kinder in der Checkliste, erledigt und gesamt — `total` 0 ohne Kinder. */
  progress: { done: number; total: number }
}

/**
 * Wo eine Aufgabe steht: Elternteil, Platz, Ort. Einhängen und Herauslösen (#204), Einrücken und
 * Ausrücken (#181) ändern genau das. `createdAt` gehört dazu: **Kurz notiert** ordnet seine Abschnitte nach dem
 * Anlegedatum — eine herausgelöste Aufgabe übernimmt dort das ihres Platzes (gemeldet 28.09.2026).
 */
export type TaskPlacement = Pick<Task, 'id' | 'parentTaskId' | 'order' | 'projectId' | 'sectionId' | 'lifeSpace' | 'createdAt'>

/**
 * Warum Einhängen, Einrücken oder Ausrücken nicht geht: `recurring` (Rhythmus unter Rhythmus),
 * `depth` (tiefer als vier Ebenen, #181), `completed` (unter eine erledigte Zeile), `invalid` (kein
 * Ziel — keine Zeile darüber, Selbstbezug, keine Aufgabe).
 */
export type NestRefusal = 'recurring' | 'invalid' | 'depth' | 'completed'

/** `before` ist der Stand vor dem Einhängen — für `⌘Z` (`restorePlacements`). */
export type NestOutcome = { refused: NestRefusal } | { before: TaskPlacement[] }

// Sync queue item for offline change tracking
export interface SyncQueueItem {
  id?: number // Auto-increment
  action: 'create' | 'update' | 'delete'
  table: 'tasks' | 'subtasks' | 'settings' | 'projects' | 'sections' | 'tags' | 'goals' | 'habits' | 'habitLogs' | 'rewardUsages' | 'userPreferences' | 'canvasNotes' | 'timeEntries' | 'taskComments' | 'projectComments' | 'taskReminders'
  recordId: number
  data: any
  synced: boolean
  timestamp: Date
  error?: string
  retryCount?: number
}

// Sync metadata for managing sync state
export interface SyncMetadata extends BaseEntity {
  tableName: string
  entityId: string
  localVersion: number
  remoteVersion: number | null
  conflictStatus: 'none' | 'local_newer' | 'remote_newer' | 'conflict'
  lastSyncAttempt: Date | null
}

// Type for creating new entities (without generated fields)
export type CreateTask = Omit<Task, keyof BaseEntity | 'order' | 'dayOrder' | 'projectId' | 'sectionId' | 'userId' | 'cloudId' | 'deletedAt' | 'durationMode' | 'startDate' | 'startTime' | 'startPeriod' | 'dueTime' | 'duePeriod' | 'rewardId' | 'recurrence'> & {
  order?: number
  dayOrder?: number | null
  projectId?: string | null
  sectionId?: string | null
  rewardId?: string | null
  recurrence?: RecurrenceRule | null
  durationMode?: DurationMode | null
  startDate?: Date | null
  startTime?: string | null
  startPeriod?: StartPeriod | null
  dueTime?: string | null
  duePeriod?: StartPeriod | null
  /**
   * Checklist typed before the task existed (creation flow only). `createTask`
   * writes these rows once it has an id — putting it there rather than at each
   * creation call site means no path can quietly drop the checklist.
   * Deliberately NOT part of {@link Task}, so `cloneTaskFields` cannot pick it
   * up; duplication goes through `db.copySubtasks`.
   */
  subtasks?: SubtaskDraft[]
  /**
   * Kommentare, die mit der Aufgabe entstehen (#182; der Todoist-Import ab #183) — im
   * selben Aufruf, damit keine übernommene Aufgabe ohne ihre Kommentare zurückbleibt.
   */
  comments?: CommentDraft[]
  /**
   * Die Erinnerungen der neuen Aufgabe. **Fehlt das Feld, bekommt eine Aufgabe mit Uhrzeit am
   * Wann die Standard-Erinnerung**; ist es gesetzt (auch leer), gilt genau diese Liste. Kopie und
   * Nachfolger reichen `[]` herein und holen die Erinnerungen der Quelle selbst (`copyReminders`,
   * `moveReminders`), der Todoist-Import die aus Todoist.
   */
  reminders?: ReminderDraft[]
}

/**
 * A subtask written with its task (`CreateTask.subtasks`). Beyond title and tick
 * the import brings the fields a child task can carry (T14 #179); place and life
 * space always come from the parent.
 */
export interface SubtaskDraft {
  title: string
  done: boolean
  importedFrom?: string | null
  completedAt?: Date | null
  description?: string | null
  attention?: AttentionLevel | null
  startDate?: Date | null
  startTime?: string | null
  dueDate?: Date | null
  estimatedDuration?: number | null
  /** Ohne Angabe `duration`, sobald eine Dauer steht — Tage (#197) muss der Import nennen. */
  durationMode?: DurationMode | null
  tags?: string[]
  /** Eigene Wiederholung (#205) — beim Todoist-Import. */
  recurrence?: RecurrenceRule | null
  /** Ihre eigenen Unteraufgaben (#181, Todoist-Import) — die Quelle legt sie eine Ebene tiefer an. */
  subtasks?: SubtaskDraft[]
  /** Ihre Kommentare (#182, Todoist-Import). */
  comments?: CommentDraft[]
  /** Ihre Erinnerungen — Regel wie {@link CreateTask.reminders}: ohne das Feld die Standard-Erinnerung, wenn sie eine Uhrzeit hat. */
  reminders?: ReminderDraft[]
  /** Übernommene Zähler (#198, Todoist-Import). */
  completedCount?: number | null
  postponedCount?: number | null
}
// `lifeSpace` stays required (not Omit'd) — every task must carry it.

export type CreateSetting = Omit<Setting, keyof BaseEntity>
export type CreateUserProfile = Omit<UserProfile, keyof BaseEntity>
export type CreateTag = Omit<Tag, keyof BaseEntity | 'usageCount' | 'userId' | 'cloudId' | 'deletedAt'> & {
  usageCount?: number
}
export type CreateProject = Omit<Project, keyof BaseEntity | 'order' | 'userId' | 'cloudId' | 'deletedAt' | 'canvasPanelState' | 'canvasSplitRatio' | 'icon' | 'iconColor' | 'nameHtml' | 'parentId'> & {
  /** Kommentare, die mit dem Projekt entstehen (Todoist-Import, #184) — im selben Aufruf. */
  comments?: CommentDraft[]
  order?: number
  userId?: string | null
  canvasPanelState?: 'closed' | 'split' | 'maximized' | null
  canvasSplitRatio?: number | null
  icon?: string | null
  iconColor?: string | null
  nameHtml?: string | null
  /** Legt das Projekt als letztes Unterprojekt unter diesem an (#185). */
  parentId?: string | null
}
export type CreateSection = Omit<Section, keyof BaseEntity | 'order' | 'userId' | 'cloudId' | 'deletedAt' | 'collapsed'> & {
  order?: number
  collapsed?: boolean
}
// `lifeSpace` and `title` stay required — every goal must carry them.
export type CreateGoal = Omit<Goal, keyof BaseEntity | 'userId' | 'cloudId' | 'deletedAt' | 'description'> & {
  userId?: string | null
  description?: string | null
}

// `title`, `lifeSpace`, `frequency` stay required — every habit must carry them.
export type CreateHabit = Omit<
  Habit,
  keyof BaseEntity | 'notes' | 'startDate' | 'rewardId' | 'targetMinutes' | 'pausedAt' | 'sortOrder' | 'userId' | 'cloudId' | 'deletedAt'
> & {
  notes?: string | null
  startDate?: Date
  rewardId?: string | null
  targetMinutes?: number | null
  sortOrder?: number
  userId?: string | null
}

// `habitId`, `date`, `completed` stay required.
export type CreateHabitLog = Omit<HabitLog, keyof BaseEntity | 'userId' | 'cloudId' | 'deletedAt'> & {
  userId?: string | null
}

// Type for updating entities (all fields optional except id)
export type UpdateTask = Partial<Omit<Task, 'id'>> & { id: string }
/**
 * Was ein Update an einer Aufgabe ändern darf — **ohne `deletedAt`** (#224). Gelöscht wird nur über
 * `deleteTask`: Nur er nimmt Unteraufgaben, Kommentare und Erinnerungen mit und meldet die Löschung
 * der Cloud. Ein Update-Payload schickt `deleted_at` nie als Zeitpunkt hoch (`sync/entities/task.ts`).
 */
export type TaskChanges = Partial<Omit<Task, 'id' | 'deletedAt'>>
export type UpdateSetting = Partial<Omit<Setting, 'id'>> & { id: string }
export type UpdateUserProfile = Partial<Omit<UserProfile, 'id'>> & { id: string }
export type UpdateTag = Partial<Omit<Tag, 'id'>> & { id: string }
export type UpdateProject = Partial<Omit<Project, 'id'>> & { id: string }
export type UpdateSection = Partial<Omit<Section, 'id'>> & { id: string }
export type UpdateGoal = Partial<Omit<Goal, 'id'>> & { id: string }
export type UpdateHabit = Partial<Omit<Habit, 'id'>> & { id: string }
export type UpdateHabitLog = Partial<Omit<HabitLog, 'id'>> & { id: string }

/**
 * Die höchste Schema-Version, die `db/schema.ts` deklariert.
 *
 * **Die Wahrheit steht dort, nicht hier** — Dexie liest die Zahl nicht, sie
 * ist eine Auskunft für Menschen. Genau deshalb war sie bis 2026-09-05 auf 24
 * stehengeblieben, während das Schema längst bei 28 war: Niemand las sie,
 * also fiel es niemandem auf. `dbVersion.test.ts` hält beide jetzt zusammen.
 */
export const DB_VERSION = 50 // Kurz notiert ohne gedachte Abschnitte (#304)
export const DB_NAME = 'happytask_db'

// Default values
export const DEFAULT_TASK_ATTENTION: AttentionLevel | null = null
export const DEFAULT_TASK_STATUS: TaskStatus = 'todo'
export const DEFAULT_TASK_CATEGORY: TaskCategory = 'other'

export const DEFAULT_USER_PREFERENCES: UserProfile['preferences'] = {
  theme: 'light', // Default to light mode (dark mode not yet implemented)
  language: 'de',
  notifications: true,
  sounds: true,
  autoSync: false,
  workingHours: {
    start: '09:00',
    end: '17:00'
  },
  pomodoroSettings: {
    workDuration: 25,
    shortBreakDuration: 5,
    longBreakDuration: 15,
    cyclesBeforeLongBreak: 4
  }
}

// Streak grace days — how many consecutive missed *due* occurrences are
// tolerated before a habit streak breaks. Global, user-configurable setting.
// 0 = strict, 1 = forgiving (default, on-brand with "ohne Druck").
export const DEFAULT_STREAK_GRACE_DAYS = 1

export const DEFAULT_USER_STATISTICS: UserProfile['statistics'] = {
  totalTasksCompleted: 0,
  totalTimeSpent: 0,
  currentStreak: 0,
  longestStreak: 0,
  lastActiveDate: null
}